import { lazy, type ReactNode, Suspense } from 'react'
import { createBrowserRouter, Outlet } from 'react-router-dom'
import { Layout } from 'src/layouts/Layout'
import UserLayout from 'src/layouts/UserLayout'
import { ModuleNameSyncer } from '@/layouts/ModuleNameSyncer'
import { RouteNavigationProgress } from '@/layouts/navigation/RouteNavigationProgress'
import coreRoutes from '@/modules/core/routes'
import { RouterErrorBoundary } from '@/modules/routes'
import wmsRoutes from '@/modules/wms/routes'

const LandingPage = lazy(() => import('@/modules/root/clusters/landing/pages/LandingPage'))
const LoginPage = lazy(() => import('@/modules/root/clusters/login/components/PageLogin'))
const ConfirmAOPage = lazy(() => import('@/modules/public/confirm-ao'))
const DeliveryConfirmationPage = lazy(() => import('@/modules/public/delivery-confirmation'))
const ArrivalOrderPrintQRPage = lazy(() => import('@/modules/public/arrival-order-print-qr'))
const NotFoundPage = lazy(() => import('@/modules/error/pages/404'))
const Error401Page = lazy(() => import('@/modules/error/pages/401'))
const Error500Page = lazy(() => import('@/modules/error/pages/500'))

import Spinner from 'src/@core/components/spinner'

function RouteFallback({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Spinner sx={{ height: 'auto', minHeight: '60vh' }} />}>{children}</Suspense>
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <Layout>
        <RouteFallback>
          <LandingPage />
        </RouteFallback>
      </Layout>
    )
  },
  {
    path: '/login',
    element: (
      <RouteFallback>
        <LoginPage />
      </RouteFallback>
    )
  },
  {
    path: '/public',
    children: [
      {
        path: 'confirm-delivery',
        element: (
          <RouteFallback>
            <ConfirmAOPage />
          </RouteFallback>
        )
      },
      {
        path: 'delivery-confirmation',
        element: (
          <RouteFallback>
            <DeliveryConfirmationPage />
          </RouteFallback>
        )
      },
      {
        path: 'arrival-order-print-qr',
        element: (
          <RouteFallback>
            <ArrivalOrderPrintQRPage />
          </RouteFallback>
        )
      }
    ]
  },
  {
    element: (
      <Layout>
        <UserLayout>
          <ModuleNameSyncer />
          <RouteNavigationProgress />
          <RouteFallback>
            <Outlet />
          </RouteFallback>
        </UserLayout>
      </Layout>
    ),
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: '/solutions/core',
        handle: { name: 'Core System' },
        children: coreRoutes
      },
      {
        path: '/solutions/wms',
        handle: { name: 'Warehouse Management System' },
        children: wmsRoutes
      },
      {
        path: '/core',
        handle: { name: 'Core System' },
        children: coreRoutes
      },
      {
        path: '/wms',
        handle: { name: 'Warehouse Management System' },
        children: wmsRoutes
      }
    ]
  },
  {
    path: '/401',
    element: (
      <RouteFallback>
        <Error401Page />
      </RouteFallback>
    )
  },
  {
    path: '/500',
    element: (
      <RouteFallback>
        <Error500Page />
      </RouteFallback>
    )
  },
  {
    path: '/404',
    element: (
      <RouteFallback>
        <NotFoundPage />
      </RouteFallback>
    )
  },
  {
    path: '*',
    element: (
      <RouteFallback>
        <NotFoundPage />
      </RouteFallback>
    )
  }
])
